<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div class="flex flex-column gap-3">
    <div class="form-field">
      <label class="form-label" for="dict-id">{{
        $t("dictionaryForm.id.label")
      }}</label>
      <PrimeInputText
        id="dict-id"
        v-model="editableDict.id"
        :disabled="isEditMode"
        class="w-full"
      />
      <small class="form-help">{{ $t("dictionaryForm.id.help") }}</small>
    </div>

    <div class="form-field">
      <label class="form-label" for="dict-label">{{
        $t("dictionaryForm.label.label")
      }}</label>
      <PrimeInputText
        id="dict-label"
        v-model="editableDict.label"
        class="w-full"
      />
      <small class="form-help">{{ $t("dictionaryForm.label.help") }}</small>
    </div>

    <div class="form-field">
      <label class="form-label">{{ $t("dictionaryForm.entries.label") }}</label>
      <small class="form-help mb-2">{{
        $t("dictionaryForm.entries.help")
      }}</small>

      <PrimeDataTable :value="editableDict.entries" size="small">
        <PrimeColumn field="key" :header="$t('dictionaryForm.entries.value')">
          <template #body="{ data }">
            <PrimeInputText v-model="data.key" class="w-full" size="small" />
          </template>
        </PrimeColumn>
        <PrimeColumn field="value" :header="$t('dictionaryForm.entries.label')">
          <template #body="{ data }">
            <PrimeInputText v-model="data.value" class="w-full" size="small" />
          </template>
        </PrimeColumn>
        <PrimeColumn headerStyle="width: 3rem">
          <template #body="slotProps">
            <PrimeButton
              icon="ri-delete-bin-line"
              :aria-label="$t('delete')"
              rounded
              text
              severity="danger"
              size="small"
              @click="removeEntry(slotProps.index)"
            />
          </template>
        </PrimeColumn>
      </PrimeDataTable>

      <PrimeButton
        :label="$t('dictionaryForm.entries.add')"
        icon="ri-add-line"
        size="small"
        severity="secondary"
        outlined
        class="mt-2"
        @click="addEntry"
      />
    </div>
  </div>
</template>

<script>
import InputText from "primevue/inputtext";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";

export default {
  name: "DictionaryForm",
  components: {
    PrimeInputText: InputText,
    PrimeDataTable: DataTable,
    PrimeColumn: Column,
    PrimeButton: Button,
  },
  props: {
    dictionary: { type: Object, default: null },
    existingDictionaries: { type: Array, default: () => [] },
  },
  emits: ["save"],
  data() {
    return {
      editableDict: { id: "", label: "", entries: [] },
      isEditMode: false,
    };
  },
  watch: {
    dictionary: {
      handler(newVal) {
        if (newVal && newVal.id) {
          this.isEditMode = true;
          this.editableDict = JSON.parse(JSON.stringify(newVal));
        } else {
          this.isEditMode = false;
          this.editableDict = newVal
            ? JSON.parse(JSON.stringify(newVal))
            : { id: "", label: "", entries: [] };
        }
        if (!this.editableDict.entries) {
          this.editableDict.entries = [];
        }
      },
      immediate: true,
    },
  },
  methods: {
    addEntry() {
      this.editableDict.entries.push({ key: "", value: "" });
    },
    removeEntry(index) {
      this.editableDict.entries.splice(index, 1);
    },
    submit() {
      if (!this.editableDict.id || !this.editableDict.label) {
        return;
      }
      if (
        !this.isEditMode &&
        this.existingDictionaries.some((d) => d.id === this.editableDict.id)
      ) {
        return;
      }
      this.editableDict.entries = this.editableDict.entries.filter(
        (e) => e.key || e.value,
      );
      this.$emit("save", { ...this.editableDict });
    },
  },
};
</script>

<style scoped>
.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.form-label {
  font-size: 0.75rem;
  color: var(--p-text-muted-color, #6b7280);
  font-weight: 600;
}

.form-help {
  font-size: 0.75rem;
  color: var(--p-text-muted-color, #6b7280);
  font-style: italic;
}
</style>
